"use client";

import React from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useTranslation, Trans } from "react-i18next";
import axiosInstance from '@/api/axios';
import { InfoTooltip } from './Infotooltip';

ChartJS.register(ArcElement, Tooltip, Legend);

// Colors are keyed by stream name (not list position), so they stay fixed
// no matter what order the backend returns the streams in. These are plain
// hex values on purpose: they must look the same in light and dark mode.
const STREAM_COLORS = {
    organic:  '#10b981', // green
    paper:    '#3b82f6', // blue
    pmd:      '#f59e0b', // orange
    residual: '#ef4444', // red
};

// Backend / legacy names that should map onto one of the four streams above.
const STREAM_ALIASES = {
    rest:     'residual',
    mixed:    'residual',
    other:    'residual',
    bio:      'organic',
    plastic:  'pmd',
    metal:    'pmd',
};

// Any stream that isn't one of the four above gets its own stable color
// (picked from this palette by name), instead of all sharing one fallback.
const EXTRA_PALETTE = ['#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#14b8a6', '#a855f7', '#eab308'];

const hashString = (s) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return h;
};

const colorFor = (name) => {
    const key = String(name || '').toLowerCase().trim();
    const resolved = STREAM_ALIASES[key] || key;
    if (STREAM_COLORS[resolved]) return STREAM_COLORS[resolved];
    return EXTRA_PALETTE[hashString(resolved) % EXTRA_PALETTE.length];
};

const HIGH_IMPACT_COLOR = '#ef4444';
const NORMAL_IMPACT_COLOR = '#10b981';

const CONTAMINATION_SHADE = 'rgba(0,0,0,0.28)';

const TZ = 'Europe/Berlin';

// "Oct 2 – Oct 3, 2026" (year once when both dates share it, otherwise on both).
const formatCustomRange = (startIso, endIso, locale) => {
    const start = new Date(startIso);
    const end   = new Date(endIso);
    const yearOf = (d) => d.toLocaleDateString('en', { year: 'numeric', timeZone: TZ });
    const sameYear = yearOf(start) === yearOf(end);
    const startLabel = start.toLocaleDateString(locale, {
        day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }), timeZone: TZ,
    });
    const endLabel = end.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: TZ });
    return `${startLabel} – ${endLabel}`;
};

// Streams shown in the legend when there's no data (no bins selected).
const EMPTY_STREAM_NAMES = ['pmd', 'organic', 'paper', 'residual'];
const EMPTY_STREAMS = Object.fromEntries(
    EMPTY_STREAM_NAMES.map((s) => [s, { waste: 0, percentage: 0, co2: 0, co2_sorted: 0, co2_contamination: 0 }])
);

// Tracks the `dark` class on <html> (what Tailwind's dark: variants use),
// so canvas drawing — which Tailwind can't style — can follow the theme.
const useIsDark = () => {
    const [isDark, setIsDark] = React.useState(
        () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
    );
    React.useEffect(() => {
        if (typeof document === 'undefined') return;
        const el = document.documentElement;
        const update = () => setIsDark(el.classList.contains('dark'));
        update();
        const observer = new MutationObserver(update);
        observer.observe(el, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);
    return isDark;
};

// Diagonal hatch drawn on top of the dark wedge, so contamination isn't
// told apart by darkness alone.
const makeHatchPattern = (ctx) => {
    const c = document.createElement('canvas');
    c.width = 6; c.height = 6;
    const p = c.getContext('2d');
    p.strokeStyle = 'rgba(0,0,0,0.35)';
    p.lineWidth = 1.2;
    p.beginPath(); p.moveTo(0, 6); p.lineTo(6, 0); p.stroke();
    return ctx.createPattern(c, 'repeat');
};

const formatCo2 = (v) => {
    const abs = Math.abs(v || 0);
    if (abs === 0) return '0';
    if (abs < 0.01) return v.toFixed(4);
    if (abs < 1) return v.toFixed(3);
    return v.toFixed(2);
};

// Weights are in kg. Below 1 kg show grams, otherwise a tidy kg value, so
// small amounts never collapse to "0.0 kg" next to a non-zero percentage.
const formatWeight = (kg) => {
    const v = Math.abs(kg || 0);
    if (v === 0) return '0 kg';
    const g = v * 1000;
    if (g < 1) return '<1 g';
    if (v < 1) return `${Math.round(g)} g`;
    return `${v.toFixed(v < 10 ? 2 : 1)} kg`;
};

// ── HTML tooltip ───────────────────────────────────────────────────────────
// Chart.js draws its default tooltip on the canvas, which sits UNDER the
// absolutely-positioned stream legend (z-10) in the middle of the card.
// Rendering the tooltip as a DOM node with a higher z-index keeps it on top,
// and lets each row carry its own swatch (solid vs. dark hatched).
const swatchStyle = (color, hatched) => ({
    display: 'inline-block',
    width: '10px',
    height: '10px',
    borderRadius: '2px',
    flexShrink: 0,
    backgroundColor: color,
    backgroundImage: hatched
        ? 'linear-gradient(rgba(0,0,0,0.28), rgba(0,0,0,0.28)), repeating-linear-gradient(135deg, rgba(0,0,0,0.35) 0 1px, transparent 1px 4px)'
        : 'none',
    border: '1px solid rgba(255,255,255,0.6)',
});

const getOrCreateTooltipEl = (chart) => {
    const parent = chart.canvas.parentNode;
    let el = parent.querySelector('div.waste-impact-tooltip');
    if (!el) {
        el = document.createElement('div');
        el.className = 'waste-impact-tooltip';
        Object.assign(el.style, {
            position: 'absolute',
            pointerEvents: 'none',
            zIndex: '30',
            background: 'rgba(17,24,39,0.92)',
            color: '#fff',
            borderRadius: '6px',
            padding: '6px 8px',
            fontSize: '12px',
            lineHeight: '1.45',
            whiteSpace: 'nowrap',
            transform: 'translate(-50%, calc(-100% - 10px))',
            transition: 'opacity 0.1s',
            opacity: '0',
        });
        parent.appendChild(el);
    }
    return el;
};

// rows: [{ text, swatch?: { color, hatched }, bold? }]
const buildTooltipRow = ({ text, swatch, bold }) => {
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '6px', fontWeight: bold ? '600' : '400' });
    if (swatch) {
        const box = document.createElement('span');
        Object.assign(box.style, swatchStyle(swatch.color, swatch.hatched));
        row.appendChild(box);
    }
    const label = document.createElement('span');
    label.textContent = text;
    row.appendChild(label);
    return row;
};

const makeExternalTooltip = (rowsForIndex) => ({ chart, tooltip }) => {
    const el = getOrCreateTooltipEl(chart);
    if (tooltip.opacity === 0 || !tooltip.dataPoints?.length) {
        el.style.opacity = '0';
        return;
    }
    const idx = tooltip.dataPoints[0].dataIndex;
    el.replaceChildren(...rowsForIndex(idx).map(buildTooltipRow));
    el.style.left = `${tooltip.caretX}px`;
    el.style.top = `${tooltip.caretY}px`;
    el.style.opacity = '1';
};

export function WasteImpactCard({ bins = [], period = 'year', year, customDateRange }) {
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
    const containerRef = React.useRef(null);
    const [containerWidth, setContainerWidth] = React.useState(420);
    const { t, i18n } = useTranslation();
    const isDark = useIsDark();

    const customDateRangeKey = (() => {
        const r = customDateRange?.[0];
        if (!r?.startDate || !r?.endDate) return null;
        const start = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
        const end   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
        return `${start}__${end}`;
    })();

    const isCustom = Boolean(customDateRangeKey);

    // Stable string so a new array reference with the same bins doesn't refetch.
    const binsKey = (bins || []).join(',');

    React.useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    React.useEffect(() => {
        if (!containerRef.current || typeof ResizeObserver === 'undefined') return;
        const el = containerRef.current;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [loading]);

    // Bumps after the card's width (or the theme) stops changing, so the pies
    // redraw at the right size / colors instead of waiting for a hover.
    const [chartKey, setChartKey] = React.useState(0);
    React.useEffect(() => {
        const id = setTimeout(() => setChartKey((k) => k + 1), 150);
        return () => clearTimeout(id);
    }, [Math.round(containerWidth), windowWidth, isDark, !data]);

    const textScale = Math.max(0.65, containerWidth / 700);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const params = { 'bins[]': bins };

                if (isCustom) {
                    const r = customDateRange[0];
                    params.start_date = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
                    params.end_date   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
                } else {
                    params.period = period;
                    if (period === 'year' && year) params.year = year;
                }

                const response = await axiosInstance.get('/waste_impact/', { params });
                setData(response.data);
            } catch (err) {
                setError(err.message || t('wasteImpact.error'));
                console.error("Error fetching waste impact data:", err);
            } finally {
                setLoading(false);
            }
        };

        if (bins.length > 0) {
            fetchData();
        } else {
            // No bins selected → clear old data so the card falls back to
            // its empty-pies state instead of showing the last selection.
            setData(null);
            setError(null);
            setLoading(false);
        }
    }, [binsKey, period, year, customDateRangeKey]);

    const getRangeLabel = () => {
        if (!data?.start_date || !data?.end_date) return null;
        const fmt = (iso) => new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric', timeZone: TZ });
        if (isCustom) return formatCustomRange(data.start_date, data.end_date, i18n.language);
        switch (period) {
            case 'day':   return fmt(data.start_date);
            case 'month': return new Date(data.start_date).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric', timeZone: TZ });
            case 'year':  return year?.toString();
            default:      return `${fmt(data.start_date)} – ${fmt(data.end_date)}`;
        }
    };

    // Skeleton placeholder — same layout as the loaded card (headline, two
    // pies, centre legend, footer) so nothing jumps when the data arrives.
    if (loading) {
        const sk = 'bg-gray-200 dark:bg-slate-700';
        const legendWidths = ['w-10', 'w-16', 'w-12', 'w-14'];
        return (
            <div className="w-full min-w-0 overflow-hidden sm:rounded-lg bg-muted/50 p-2" aria-busy="true">
                <span className="sr-only" role="status">{t('wasteImpact.loading')}</span>

                <div className="flex items-center justify-between">
                    <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">{t('wasteImpact.title')}</h2>
                    <div className="flex items-center gap-2 motion-safe:animate-pulse">
                        <div className={`h-5 w-5 rounded-full ${sk}`} />
                    </div>
                </div>

                <div className="aspect-[4/3] md:aspect-[16/9] motion-safe:animate-pulse">
                    <div className="flex flex-col h-full w-full">
                        <div className="flex justify-center py-3">
                            <div className={`h-4 w-3/4 max-w-md rounded ${sk}`} />
                        </div>

                        <div className="flex-1 grid grid-cols-2 relative min-h-0">
                            {[0, 1].map((i) => (
                                <div key={i} className="flex flex-col items-center h-full min-h-0 pt-0.5">
                                    <div className={`h-3 w-16 rounded ${sk}`} />
                                    <div className="relative flex-1 w-full min-h-[110px]">
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className={`h-[70%] aspect-square rounded-full ${sk}`} />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <div className="absolute left-1/2 -translate-x-1/2 bottom-1/3 z-10 flex flex-col gap-2 rounded-lg bg-white/80 dark:bg-gray-800/80 px-3 py-2">
                                {legendWidths.map((w) => (
                                    <div key={w} className="flex items-center gap-2">
                                        <div className={`h-3 w-3 rounded-full ${sk}`} />
                                        <div className={`h-3 ${w} rounded ${sk}`} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-col items-center gap-1.5 mt-1">
                            <div className={`h-3 w-40 rounded ${sk}`} />
                            <div className={`h-3 w-52 rounded ${sk}`} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) return (
        <div className="w-full min-w-0 overflow-hidden sm:rounded-lg bg-muted/50 p-2">
            <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">{t('wasteImpact.title')}</h2>
            <div className="aspect-[4/3] md:aspect-[16/9] flex flex-col items-center justify-center p-4">
                <p className="text-red-500 mb-4 text-center">{error}</p>
            </div>
        </div>
    );

    // No data (no bins selected): same layout, but the pies are empty grey
    // circles with no labels, and the headline / footer are left blank.
    const isEmpty = !data;
    const streams = data?.waste_streams || EMPTY_STREAMS;
    const totalCo2 = data?.total_co2 || 0;

    const wasteData = Object.entries(streams).map(([wasteType, stats]) => ({
        name: wasteType, value: stats.waste, percentage: stats.percentage, unit: 'kg'
    }));

    // Impact = CO2e actually produced. co2_contamination is the part caused
    // by contaminants (burned as residual anyway) — drawn as the dark wedge.
    const ghgData = Object.entries(streams).map(([wasteType, stats]) => ({
        name: wasteType,
        value: stats.co2,
        co2Sorted: stats.co2_sorted || 0,
        co2Contamination: stats.co2_contamination || 0,
        contamFrac: stats.co2 > 0 ? (stats.co2_contamination || 0) / stats.co2 : 0,
        percentage: totalCo2 > 0 ? (stats.co2 / totalCo2) * 100 : 0,
        unit: 'kg CO₂e'
    }));

    const highestGHGStream = ghgData.reduce((max, item) =>
        item.percentage > max.percentage ? item : max, { name: '', percentage: 0 }
    );
    const wastePercentageForHighestGHG = wasteData.find(item => item.name === highestGHGStream.name)?.percentage || 0;
    const impactRatio = wastePercentageForHighestGHG > 0
        ? (highestGHGStream.percentage / wastePercentageForHighestGHG).toFixed(1)
        : '0.0';
    const isHighImpact = Number(impactRatio) > 1.5;
    const impactColor = isHighImpact ? HIGH_IMPACT_COLOR : NORMAL_IMPACT_COLOR;

    const getTranslatedStreamName = (streamName) =>
        t(`wasteImpact.streams.${streamName.toLowerCase().trim()}`, streamName);

    const emptySliceColor = isDark ? '#334155' : '#e5e7eb';

    const wasteChartData = isEmpty
        ? { labels: [''], datasets: [{ data: [1], backgroundColor: [emptySliceColor], borderWidth: 0 }] }
        : {
            labels: wasteData.map(item => getTranslatedStreamName(item.name)),
            datasets: [{
                data: wasteData.map(item => item.value),
                backgroundColor: wasteData.map(item => colorFor(item.name)),
                borderWidth: 0,
            }]
        };

    const ghgChartData = isEmpty
        ? { labels: [''], datasets: [{ data: [1], backgroundColor: [emptySliceColor], borderWidth: 0 }] }
        : {
            labels: ghgData.map(item => getTranslatedStreamName(item.name)),
            datasets: [{
                data: ghgData.map(item => item.value),
                backgroundColor: ghgData.map(item => colorFor(item.name)),
                borderWidth: 0,
            }]
        };

    // Dark wedge from the centre of each Impact slice. Area ∝ the
    // contamination share of that slice's CO2e (radius = R·√share).
    const contaminationOverlayPlugin = {
        id: 'contaminationOverlay',
        afterDatasetsDraw: (chart) => {
            if (isEmpty) return;
            if (!chart.canvas.id.includes('ghg')) return;
            const ctx = chart.ctx;
            const meta = chart.getDatasetMeta(0).data;
            if (!meta.length) return;
            const hatch = makeHatchPattern(ctx);

            meta.forEach((arc, i) => {
                const frac = Math.min(ghgData[i]?.contamFrac || 0, 1);
                if (frac <= 0) return;
                const r = arc.outerRadius * Math.sqrt(frac);

                ctx.save();
                ctx.beginPath();
                ctx.moveTo(arc.x, arc.y);
                ctx.arc(arc.x, arc.y, r, arc.startAngle, arc.endAngle);
                ctx.closePath();
                ctx.fillStyle = CONTAMINATION_SHADE;
                ctx.fill();
                ctx.fillStyle = hatch;
                ctx.fill();
                ctx.restore();
            });
        }
    };

    // Percentage labels are drawn on the canvas, so they need theme-aware
    // colors here (Tailwind dark: classes don't apply). A thin outline in the
    // background color keeps them readable over slices without the blurry
    // glow a shadowBlur gives.
    const labelFill    = isDark ? '#f9fafb' : '#374151';
    const labelOutline = isDark ? 'rgba(17,24,39,0.9)' : 'rgba(255,255,255,0.9)';

    const pieChartPercentagePlugin = {
        id: 'pieChartPercentage',
        afterDraw: (chart) => {
            if (isEmpty) return;
            const ctx = chart.ctx;
            const percentageData = chart.canvas.id.includes('waste')
                ? wasteData.map(item => item.percentage)
                : ghgData.map(item => item.percentage);

            const meta = chart.getDatasetMeta(0).data;
            if (!meta.length) return;
            const chartRadius = meta[0].outerRadius;
            const fontSize = Math.max(8, Math.min(16, Math.round(chartRadius * 0.22)));

            const minPercentToLabel = windowWidth < 480 ? 8 : 5;

            const placed = [];

            meta.forEach((element, index) => {
                const pct = percentageData[index];
                if (pct < minPercentToLabel) return;

                const percent = pct.toFixed(1) + '%';
                const midAngle = (element.startAngle + element.endAngle) / 2;
                let radiusMultiplier = 1.2;
                let x, y;

                for (let attempt = 0; attempt < 3; attempt++) {
                    const radius = element.outerRadius * radiusMultiplier;
                    x = element.x + Math.cos(midAngle) * radius;
                    y = element.y + Math.sin(midAngle) * radius;
                    const collides = placed.some(
                        (p) => Math.abs(p.x - x) < fontSize * 2 && Math.abs(p.y - y) < fontSize * 1.4
                    );
                    if (!collides) break;
                    radiusMultiplier += 0.18;
                }
                placed.push({ x, y });

                // Whole pixels keep the text crisp on the canvas.
                const px = Math.round(x);
                const py = Math.round(y);

                ctx.save();
                ctx.font = `bold ${fontSize}px Arial`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.lineJoin = 'round';
                ctx.lineWidth = 3;
                ctx.strokeStyle = labelOutline;
                ctx.strokeText(percent, px, py);
                ctx.fillStyle = labelFill;
                ctx.fillText(percent, px, py);
                ctx.restore();
            });
        }
    };

    const volumeTooltipRows = (i) => {
        const item = wasteData[i];
        const color = colorFor(item.name);
        return [
            { text: getTranslatedStreamName(item.name).toUpperCase(), swatch: { color }, bold: true },
            { text: `${t('wasteImpact.tooltip.amount')}: ${formatWeight(item.value)}` },
            { text: `${t('wasteImpact.tooltip.percentage')}: ${item.percentage.toFixed(1)}%` },
        ];
    };

    const impactTooltipRows = (i) => {
        const item = ghgData[i];
        const color = colorFor(item.name);
        return [
            { text: getTranslatedStreamName(item.name).toUpperCase(), bold: true },
            { text: `${t('wasteImpact.tooltip.total', 'Total')}: ${formatCo2(item.value)} ${item.unit}` },
            { text: `${t('wasteImpact.tooltip.sorted', 'From sorted waste')}: ${formatCo2(item.co2Sorted)} ${item.unit}`, swatch: { color } },
            { text: `${t('wasteImpact.tooltip.contamination', 'From contamination')}: ${formatCo2(item.co2Contamination)} ${item.unit}`, swatch: { color, hatched: true } },
            { text: `${t('wasteImpact.tooltip.percentage')}: ${item.percentage.toFixed(1)}%` },
        ];
    };

    const makeChartOptions = (rowsForIndex) => ({
        responsive: true,
        maintainAspectRatio: false,
        // Empty pies are decorative: no hover, no tooltip.
        events: isEmpty ? [] : undefined,
        plugins: {
            tooltip: isEmpty
                ? { enabled: false }
                : { enabled: false, external: makeExternalTooltip(rowsForIndex) },
            legend: { display: false },
            datalabels: { display: false }
        },
        cutout: 0,
        radius: '70%'
    });

    const focusStream = data?.focus_stream;
    const smallFont = `${Math.max(8, Math.round(13 * textScale))}px`;
    const swatchSize = `${Math.max(6, Math.round(12 * textScale))}px`;
    // Below this, two side-by-side pies get too narrow to read (labels
    // collide with the floating centre legend). Stack them instead and
    // move the legend into normal flow so nothing overlaps.
    const isNarrow = containerWidth < 380;

    // Headline: the numbers and the stream name share the stream's chart
    // color (inline hex, so it can't be washed out by the theme).
    const headlineColor = colorFor(highestGHGStream.name);
    const headlineHighlight = { fontWeight: 600, color: headlineColor };
    const headlineStreamName = getTranslatedStreamName(highestGHGStream.name).toLocaleLowerCase(i18n.language);

    return (
        // min-w-0 + overflow-hidden: when the sidebar toggles, the card width
        // changes but the window width doesn't. Without these, the grid items
        // (min-width: auto) refuse to shrink below the canvas's old size and
        // the pie spills out of the card.
        <div ref={containerRef} className="w-full min-w-0 overflow-hidden sm:rounded-lg bg-muted/50 p-2">
            {/* Header row — same structure/scaling as CO2SavingsCard's header:
                flex-wrap so title + badges + icon reflow instead of
                clipping, title font sized from textScale instead of a
                fixed Tailwind class. */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                    <h2
                        className="scroll-m-20 font-semibold tracking-tight"
                        style={{ fontSize: `${Math.round(20 * textScale)}px` }}
                    >
                        {t('wasteImpact.title')}
                    </h2>
                    {isCustom && getRangeLabel() && (
                        <span className="text-xs text-muted-foreground">{getRangeLabel()}</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <InfoTooltip text="Left: how much of each stream you produce by weight. Right: the CO₂ each stream actually causes. The darker part of each slice is CO₂ caused by contamination, since contaminated loads end up burned as residual." />
                </div>
            </div>

            <div className={isNarrow ? '' : 'aspect-[4/3] md:aspect-[16/9]'}>
                <div className="flex flex-col h-full w-full min-w-0 gap-0">
                    <div className="px-2 py-2">
                        <div className="text-center">
                            <p
                                className="font-medium text-foreground leading-relaxed"
                                style={{ fontSize: `${Math.round(16 * textScale)}px` }}
                            >
                                {isEmpty ? (
                                    // Keeps the headline's height so the pies don't jump.
                                    <span aria-hidden="true">&nbsp;</span>
                                ) : (
                                    <Trans
                                        i18nKey="wasteImpact.headline"
                                        defaults="<waste>{{wastePct}}%</waste> of your <stream>{{stream}}</stream> waste is responsible for <impact>{{co2Pct}}%</impact> of your environmental CO₂"
                                        values={{
                                            wastePct: Math.round(wastePercentageForHighestGHG),
                                            stream: headlineStreamName,
                                            co2Pct: Math.round(highestGHGStream.percentage),
                                        }}
                                        components={{
                                            waste:  <span style={headlineHighlight} />,
                                            stream: <span style={headlineHighlight} />,
                                            impact: <span style={headlineHighlight} />,
                                        }}
                                    />
                                )}
                            </p>
                        </div>
                    </div>

                    <div className={`flex-1 grid ${isNarrow ? 'grid-cols-1' : 'grid-cols-2'} gap-0 relative min-h-0 min-w-0`}>
                        <div className="flex flex-col h-full pt-0.5 min-h-0 min-w-0">
                            <h3
                                className="font-medium text-center text-muted-foreground"
                                style={{ fontSize: smallFont }}
                            >
                                {t('wasteImpact.labels.volume').toUpperCase()}
                            </h3>
                            <div className="flex-1 min-h-[110px] min-w-0 relative">
                                <div className="absolute inset-0">
                                    <Pie key={chartKey} data={wasteChartData} options={makeChartOptions(volumeTooltipRows)} plugins={[pieChartPercentagePlugin]} id="waste-chart" />
                                </div>
                            </div>
                        </div>

                        {!isNarrow && (
                            <div className="absolute left-1/2 transform -translate-x-1/2 bottom-1/3 flex flex-col items-center z-10">
                                <div
                                    className="bg-white/80 dark:bg-gray-800/80 rounded-lg shadow-sm"
                                    style={{ padding: `${Math.round(4 * textScale)}px ${Math.round(8 * textScale)}px` }}
                                >
                                    <ul className="flex flex-col" style={{ gap: `${Math.max(1, Math.round(4 * textScale))}px` }}>
                                        {wasteData.map((item) => (
                                            <li key={item.name} className="flex items-center" style={{ gap: `${Math.round(6 * textScale)}px` }}>
                                                <span
                                                    className="rounded-full flex-shrink-0"
                                                    style={{
                                                        backgroundColor: colorFor(item.name),
                                                        width: swatchSize,
                                                        height: swatchSize,
                                                    }}
                                                />
                                                <span
                                                    className="font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
                                                    style={{ fontSize: smallFont }}
                                                >
                                                    {getTranslatedStreamName(item.name).toUpperCase()}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {isNarrow && (
                            <div className="flex justify-center py-1">
                                <ul className="flex flex-wrap justify-center" style={{ gap: `${Math.round(4 * textScale)}px ${Math.round(10 * textScale)}px` }}>
                                    {wasteData.map((item) => (
                                        <li key={item.name} className="flex items-center" style={{ gap: `${Math.round(4 * textScale)}px` }}>
                                            <span
                                                className="rounded-full flex-shrink-0"
                                                style={{
                                                    backgroundColor: colorFor(item.name),
                                                    width: swatchSize,
                                                    height: swatchSize,
                                                }}
                                            />
                                            <span
                                                className="font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
                                                style={{ fontSize: smallFont }}
                                            >
                                                {getTranslatedStreamName(item.name).toUpperCase()}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="flex flex-col h-full pt-0.5 min-h-0 min-w-0">
                            <h3
                                className="font-medium text-center text-muted-foreground"
                                style={{ fontSize: smallFont }}
                            >
                                {t('wasteImpact.labels.impact').toUpperCase()}
                            </h3>
                            <div className="flex-1 min-h-[110px] min-w-0 relative">
                                <div className="absolute inset-0">
                                    <Pie key={chartKey} data={ghgChartData} options={makeChartOptions(impactTooltipRows)} plugins={[contaminationOverlayPlugin, pieChartPercentagePlugin]} id="ghg-chart" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {!isEmpty && (
                        <div className="flex flex-col items-center mt-0.5" style={{ gap: `${Math.round(2 * textScale)}px` }}>
                            <div className="flex items-center text-muted-foreground" style={{ gap: `${Math.round(6 * textScale)}px`, fontSize: smallFont }}>
                                <span
                                    className="rounded-sm flex-shrink-0"
                                    style={{
                                        width: swatchSize,
                                        height: swatchSize,
                                        backgroundColor: '#6b7280',
                                        backgroundImage: 'repeating-linear-gradient(135deg, rgba(0,0,0,0.35) 0 1px, transparent 1px 4px)',
                                    }}
                                />
                                <span>{t('wasteImpact.contaminationLegend', 'Darker part = CO₂ from contamination')}</span>
                            </div>
                            <p className="font-medium text-foreground text-center" style={{ fontSize: smallFont }}>
                                <span style={{ color: impactColor }}>{t('wasteImpact.focus')}:</span>
                                {' '}
                                {focusStream
                                    ? <>{t('wasteImpact.reduceContamination', 'Reduce contamination in')} {getTranslatedStreamName(focusStream)}</>
                                    : <>{t('wasteImpact.reduce')} {getTranslatedStreamName(highestGHGStream.name)}</>}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}